import { useEffect, useState } from 'react';
import api from '../services/api';
import useLang from '../hooks/useLang';

const MEMBER_KEYS = ['transactions', 'fines', 'changiwa', 'monthly_report', 'distribution'];
const ADMIN_KEYS  = [
  'add_transactions', 'delete_transactions',
  'add_members', 'deactivate_members', 'delete_members',
  'manage_fines', 'manage_changiwa', 'manage_expenses', 'import_csv',
];

const MEMBER_ICONS = {
  transactions: '💳', fines: '⚠️', changiwa: '✨',
  monthly_report: '📅', distribution: '📊',
};
const ADMIN_ICONS = {
  add_transactions: '➕', delete_transactions: '🗑️',
  add_members: '👤', deactivate_members: '🚫', delete_members: '❌',
  manage_fines: '⚡', manage_changiwa: '🎗️', manage_expenses: '📋', import_csv: '📤',
};

const MEMBER_DEFAULTS = Object.fromEntries(MEMBER_KEYS.map(k => [k, true]));
const ADMIN_DEFAULTS  = Object.fromEntries(ADMIN_KEYS.map(k => [k, true]));

function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative w-12 h-6 rounded-full transition-colors focus:outline-none ${checked ? 'bg-green-500' : 'bg-gray-300'}`}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-0'}`} />
    </button>
  );
}

function PermRow({ icon, label, desc, checked, onChange }) {
  return (
    <div className={`flex items-center justify-between p-3 rounded-xl border transition-colors ${checked ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-xl shrink-0">{icon}</span>
        <div className="min-w-0">
          <p className="font-medium text-gray-800 text-sm">{label}</p>
          <p className="text-xs text-gray-400 truncate">{desc}</p>
        </div>
      </div>
      <div className="shrink-0 ml-3">
        <Toggle checked={checked} onChange={onChange} />
      </div>
    </div>
  );
}

export default function Permissions() {
  const { T } = useLang();
  const Tp = T.perms;

  const [groups, setGroups]           = useState([]);
  const [selected, setSelected]       = useState('');
  const [memberPerms, setMemberPerms] = useState(MEMBER_DEFAULTS);
  const [adminPerms, setAdminPerms]   = useState(ADMIN_DEFAULTS);
  const [loading, setLoading]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [saved, setSaved]             = useState(false);

  useEffect(() => {
    api.get('/groups').then(r => {
      setGroups(r.data || []);
      if (r.data?.length) setSelected(String(r.data[0].id));
    });
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    api.get(`/groups/${selected}/permissions`)
      .then(r => {
        setMemberPerms({ ...MEMBER_DEFAULTS, ...(r.data.member || {}) });
        setAdminPerms({ ...ADMIN_DEFAULTS, ...(r.data.admin || {}) });
      })
      .finally(() => setLoading(false));
  }, [selected]);

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    setSaved(false);
    try {
      await api.put(`/groups/${selected}/permissions`, { member: memberPerms, admin: adminPerms });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  const toggleAll = (type, value) => {
    if (type === 'member') {
      setMemberPerms(Object.fromEntries(MEMBER_KEYS.map(k => [k, value])));
    } else {
      setAdminPerms(Object.fromEntries(ADMIN_KEYS.map(k => [k, value])));
    }
  };

  const selectedGroup = groups.find(g => String(g.id) === selected);

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      {/* Header */}
      <div className="overflow-hidden rounded-[30px] border border-white/70 bg-[linear-gradient(135deg,rgba(79,70,229,0.96),rgba(15,23,42,0.94))] px-5 py-6 text-white shadow-[0_24px_60px_rgba(15,23,42,0.18)]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-indigo-200/80">Superadmin</p>
            <h1 className="mt-2 text-2xl font-bold sm:text-3xl">{Tp.title}</h1>
            <p className="mt-2 text-sm text-indigo-50/80">{Tp.subtitle}</p>
          </div>
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[24px] bg-white/12 text-3xl">🔐</div>
        </div>
      </div>

      {/* Group selector */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <label className="block text-sm font-semibold text-gray-700 mb-2">{Tp.selectGroup}</label>
        <select
          value={selected}
          onChange={e => setSelected(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">{T.common.loading}</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {/* Member permissions */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-bold text-gray-800 text-base">👤 {Tp.memberTitle}</h2>
                <p className="text-xs text-gray-400 mt-0.5">{Tp.memberDesc}</p>
              </div>
              <div className="flex gap-1 text-xs">
                <button onClick={() => toggleAll('member', true)} className="text-green-600 hover:underline">{Tp.allOn}</button>
                <span className="text-gray-300">|</span>
                <button onClick={() => toggleAll('member', false)} className="text-red-500 hover:underline">{Tp.allOff}</button>
              </div>
            </div>
            <div className="space-y-2">
              {MEMBER_KEYS.map(k => (
                <PermRow
                  key={k}
                  icon={MEMBER_ICONS[k]}
                  label={Tp.member[k].label}
                  desc={Tp.member[k].desc}
                  checked={!!memberPerms[k]}
                  onChange={val => setMemberPerms(p => ({ ...p, [k]: val }))}
                />
              ))}
            </div>
          </div>

          {/* Admin permissions */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-bold text-gray-800 text-base">👑 {Tp.adminTitle}</h2>
                <p className="text-xs text-gray-400 mt-0.5">{Tp.adminDesc}</p>
              </div>
              <div className="flex gap-1 text-xs">
                <button onClick={() => toggleAll('admin', true)} className="text-green-600 hover:underline">{Tp.allOn}</button>
                <span className="text-gray-300">|</span>
                <button onClick={() => toggleAll('admin', false)} className="text-red-500 hover:underline">{Tp.allOff}</button>
              </div>
            </div>
            <div className="space-y-2">
              {ADMIN_KEYS.map(k => (
                <PermRow
                  key={k}
                  icon={ADMIN_ICONS[k]}
                  label={Tp.admin[k].label}
                  desc={Tp.admin[k].desc}
                  checked={!!adminPerms[k]}
                  onChange={val => setAdminPerms(p => ({ ...p, [k]: val }))}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Save bar */}
      {!loading && selected && (
        <div className="sticky bottom-4 flex items-center justify-between bg-white border border-gray-200 rounded-2xl shadow-lg px-5 py-4">
          <div>
            <p className="text-sm font-semibold text-gray-800">{selectedGroup?.name}</p>
            <p className="text-xs text-gray-400">{Tp.saveHint}</p>
          </div>
          <div className="flex items-center gap-3">
            {saved && <span className="text-sm text-green-600 font-medium">✓ {Tp.saved}</span>}
            <button
              onClick={handleSave}
              disabled={saving || !selected}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-60 transition-colors"
            >
              {saving ? T.common.saving : Tp.save}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
